import { useState, useRef, useEffect } from 'react';
import { ArrowDown, ArrowUp, Layers, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  type ListViewPreferences,
  type ListViewGroupBy,
  type ListViewSortDirection,
  DEFAULT_LIST_VIEW_PREFS,
} from './list-view-prefs';
import { cn } from '@/lib/utils';

const GROUP_BY_OPTIONS: { value: ListViewGroupBy; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'status', label: 'Status' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'priority', label: 'Priority' },
  { value: 'tags', label: 'Tags' },
  { value: 'dueDate', label: 'Due date' },
  { value: 'list', label: 'List' },
  { value: 'lastUpdated', label: 'Last updated' },
];

function groupByLabel(value: ListViewGroupBy): string {
  return GROUP_BY_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

interface ListViewToolbarProps {
  prefs: ListViewPreferences;
  onPrefsChange: (prefs: ListViewPreferences) => void;
  onReset: () => void;
}

export function ListViewToolbar({ prefs, onPrefsChange, onReset }: ListViewToolbarProps) {
  const handleGroupBy = (value: ListViewGroupBy) => {
    const next: ListViewPreferences = {
      ...prefs,
      groupBy: value,
      ...(value === 'lastUpdated' && prefs.groupBy !== 'lastUpdated'
        ? { sortDirection: 'desc' }
        : {}),
    };
    onPrefsChange(next);
  };

  const handleSortDirection = (direction: ListViewSortDirection) => {
    const next = { ...prefs, sortDirection: direction };
    onPrefsChange(next);
  };

  const handleAlsoGroupByList = (checked: boolean) => {
    const next = { ...prefs, alsoGroupByList: checked };
    onPrefsChange(next);
  };

  const handleShowClosedTasks = (checked: boolean) => {
    const next = { ...prefs, showClosedTasks: checked };
    onPrefsChange(next);
  };

  const handleReset = () => {
    onPrefsChange(DEFAULT_LIST_VIEW_PREFS);
    onReset();
  };

  const isDefault =
    prefs.groupBy === DEFAULT_LIST_VIEW_PREFS.groupBy &&
    prefs.sortDirection === DEFAULT_LIST_VIEW_PREFS.sortDirection &&
    prefs.alsoGroupByList === DEFAULT_LIST_VIEW_PREFS.alsoGroupByList &&
    prefs.visibleCustomFieldIds.length === 0 &&
    prefs.tableSortColumn === null &&
    prefs.collapsedGroups.length === 0 &&
    !prefs.showClosedTasks;

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-b border-border bg-card/50">
      {/* Group by */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
            <Layers className="h-3.5 w-3.5" />
            Group by: {groupByLabel(prefs.groupBy)}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[140px]">
          {GROUP_BY_OPTIONS.map((opt) => (
            <DropdownMenuItem
              key={opt.value}
              onClick={() => handleGroupBy(opt.value)}
              className={cn(prefs.groupBy === opt.value && 'bg-accent')}
            >
              {opt.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Sort direction only (field is determined by Group by) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
            {prefs.sortDirection === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5" />
            )}
            Sort: {prefs.sortDirection === 'asc' ? 'Ascending' : 'Descending'}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[140px]">
          <DropdownMenuItem
            onClick={() => handleSortDirection('asc')}
            className={cn(prefs.sortDirection === 'asc' && 'bg-accent')}
          >
            <ArrowUp className="h-3.5 w-3.5 mr-2" />
            Ascending
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleSortDirection('desc')}
            className={cn(prefs.sortDirection === 'desc' && 'bg-accent')}
          >
            <ArrowDown className="h-3.5 w-3.5 mr-2" />
            Descending
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Also group by List */}
      <div className="flex items-center gap-2">
        <Switch
          id="also-group-by-list"
          checked={prefs.alsoGroupByList}
          onCheckedChange={handleAlsoGroupByList}
          className="scale-90"
        />
        <Label htmlFor="also-group-by-list" className="text-xs text-muted-foreground cursor-pointer">
          Also group by List
        </Label>
      </div>

      {/* Show closed tasks */}
      <div className="flex items-center gap-2">
        <Switch
          id="show-closed-tasks"
          checked={prefs.showClosedTasks ?? false}
          onCheckedChange={handleShowClosedTasks}
          className="scale-90"
        />
        <Label htmlFor="show-closed-tasks" className="text-xs text-muted-foreground cursor-pointer">
          Show closed tasks
        </Label>
      </div>

      {/* Reset */}
      <Button
        variant="ghost"
        size="sm"
        className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        onClick={handleReset}
        disabled={isDefault}
      >
        <RotateCcw className="h-3.5 w-3.5" />
        Reset
      </Button>
    </div>
  );
}

export function ListViewColumnHeader({
  field,
  onRemove,
  canRemove,
  onRename,
  canRename,
}: {
  field: { id: string; name: string };
  onRemove: () => void;
  canRemove: boolean;
  onRename?: (fieldId: string, newName: string) => void;
  canRename?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(field.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      setEditValue(field.name);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing, field.name]);

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== field.name && onRename) {
      onRename(field.id, trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditValue(field.name);
      setIsEditing(false);
    }
  };

  return (
    <div className="flex items-center gap-1 min-w-0">
      {isEditing ? (
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="h-6 text-xs min-w-0"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (canRename && onRename) setIsEditing(true);
          }}
          className={cn(
            'truncate text-xs text-left min-w-0 flex-1',
            canRename && onRename && 'hover:bg-accent/50 rounded px-1 -mx-1 cursor-pointer'
          )}
        >
          {field.name}
        </button>
      )}
      {canRemove && !isEditing && (
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0 opacity-70 hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
